import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../../institutionAdmin.endpoints";
import type {
  HostelBuilding, HostelRoom, HostelAllocation, HostelBed, HostelStaffAssignment, HostelSettings,
  HostelBuildingType, HostelRoomType, HostelRoomStatus, HostelStaffRole, HostelShift,
} from "../types";

const HOSTEL = INSTITUTION_ADMIN_ENDPOINTS.FACILITIES.HOSTEL;

function serverError(e: any): never {
  throw new Error(e?.response?.data?.error || e?.message || "Request failed");
}

export async function fetchBuildings(institutionId: number): Promise<HostelBuilding[]> {
  const { data } = await privateClient.get(HOSTEL.BUILDINGS(institutionId)).catch(serverError);
  return data.buildings ?? [];
}

export async function createBuilding(institutionId: number, payload: { name: string; type?: HostelBuildingType; floors?: number | null; monthlyFee?: number | null }): Promise<HostelBuilding> {
  const { data } = await privateClient.post(HOSTEL.BUILDINGS(institutionId), payload).catch(serverError);
  return data.building;
}

export async function fetchRooms(institutionId: number, buildingId?: number): Promise<HostelRoom[]> {
  const { data } = await privateClient.get(HOSTEL.ROOMS(institutionId), { params: { buildingId } }).catch(serverError);
  return data.rooms ?? [];
}

export async function createRoom(
  institutionId: number,
  payload: { buildingId: number; roomNumber: string; capacity?: number; floor?: number | null; roomType?: HostelRoomType; monthlyFee?: number | null }
): Promise<HostelRoom> {
  const { data } = await privateClient.post(HOSTEL.ROOMS(institutionId), payload).catch(serverError);
  return data.room;
}

export async function allocate(
  institutionId: number,
  payload: { roomId: number; studentId: number; bedId?: number; feeAmount?: number | null; academicYearId?: number }
): Promise<HostelAllocation> {
  const { data } = await privateClient.post(HOSTEL.ALLOCATE(institutionId), payload).catch(serverError);
  return data.allocation;
}

export async function vacate(institutionId: number, allocationId: number): Promise<HostelAllocation> {
  const { data } = await privateClient.post(HOSTEL.VACATE(institutionId, allocationId)).catch(serverError);
  return data.allocation;
}

export async function fetchStudentAllocations(
  institutionId: number,
  studentId: number,
  academicYearId?: number
): Promise<HostelAllocation[]> {
  const { data } = await privateClient.get(HOSTEL.STUDENT_ALLOCATION(institutionId, studentId), { params: { academicYearId } }).catch(serverError);
  return data.allocations ?? [];
}

export async function updateBuilding(
  institutionId: number,
  buildingId: number,
  payload: { name?: string; type?: HostelBuildingType; floors?: number | null; monthlyFee?: number | null }
): Promise<HostelBuilding> {
  const { data } = await privateClient.patch(HOSTEL.BUILDING(institutionId, buildingId), payload).catch(serverError);
  return data.building;
}

export async function deleteBuilding(institutionId: number, buildingId: number): Promise<void> {
  await privateClient.delete(HOSTEL.BUILDING(institutionId, buildingId)).catch(serverError);
}

export async function updateRoom(
  institutionId: number,
  roomId: number,
  payload: { roomNumber?: string; capacity?: number; floor?: number | null; roomType?: HostelRoomType; status?: HostelRoomStatus; monthlyFee?: number | null }
): Promise<HostelRoom> {
  const { data } = await privateClient.patch(HOSTEL.ROOM(institutionId, roomId), payload).catch(serverError);
  return data.room;
}

export async function deleteRoom(institutionId: number, roomId: number): Promise<void> {
  await privateClient.delete(HOSTEL.ROOM(institutionId, roomId)).catch(serverError);
}

export async function fetchRoomBeds(institutionId: number, roomId: number): Promise<HostelBed[]> {
  const { data } = await privateClient.get(HOSTEL.ROOM_BEDS(institutionId, roomId)).catch(serverError);
  return data.beds ?? [];
}

export async function transfer(
  institutionId: number,
  allocationId: number,
  payload: { roomId: number; bedId?: number }
): Promise<HostelAllocation> {
  const { data } = await privateClient.post(HOSTEL.TRANSFER(institutionId, allocationId), payload).catch(serverError);
  return data.allocation;
}

export async function fetchStaff(institutionId: number): Promise<HostelStaffAssignment[]> {
  const { data } = await privateClient.get(HOSTEL.STAFF(institutionId)).catch(serverError);
  return data.staff ?? [];
}

export async function assignStaff(
  institutionId: number,
  payload: { staffId: number; buildingId?: number | null; role?: HostelStaffRole; shift?: HostelShift }
): Promise<HostelStaffAssignment> {
  const { data } = await privateClient.post(HOSTEL.STAFF(institutionId), payload).catch(serverError);
  return data.assignment;
}

export async function endStaffAssignment(institutionId: number, assignmentId: number): Promise<void> {
  await privateClient.post(HOSTEL.STAFF_END(institutionId, assignmentId)).catch(serverError);
}

export async function fetchSettings(institutionId: number): Promise<HostelSettings> {
  const { data } = await privateClient.get(HOSTEL.SETTINGS(institutionId)).catch(serverError);
  return data.settings;
}

export async function updateSettings(institutionId: number, payload: HostelSettings): Promise<HostelSettings> {
  const { data } = await privateClient.put(HOSTEL.SETTINGS(institutionId), payload).catch(serverError);
  return data.settings;
}

export async function setAllocationFee(institutionId: number, allocationId: number, feeAmount: number | null): Promise<HostelAllocation> {
  const { data } = await privateClient.patch(HOSTEL.ALLOCATION_FEE(institutionId, allocationId), { feeAmount }).catch(serverError);
  return data.allocation;
}

export interface CarryForwardPreview {
  academicYearId: number;
  academicYearLabel: string;
  residents: number;
  messMembers: number;
}

export async function fetchCarryForward(institutionId: number): Promise<CarryForwardPreview> {
  const { data } = await privateClient.get(`${HOSTEL.BASE(institutionId)}/carry-forward`).catch(serverError);
  return data;
}

export async function runCarryForward(institutionId: number): Promise<{ academicYearLabel: string; residents: number; messMembers: number; failed: number }> {
  const { data } = await privateClient.post(`${HOSTEL.BASE(institutionId)}/carry-forward`).catch(serverError);
  return data;
}

export interface HostelAccess {
  level: "ADMIN" | "ACCESS" | "INCHARGE" | "WARDEN" | "NONE";
  isAdmin: boolean;
  canCreateBuildings: boolean;
  canEditStructure: boolean;
  canAppoint: boolean;
  buildings: { id: number; name: string; type: HostelBuildingType; level: "ACCESS" | "INCHARGE" | "WARDEN"; canEditStructure: boolean; canAppoint: boolean }[];
}

/** What the signed-in person may do in the hostel, so the screen shows only what applies. */
export async function fetchMyAccess(institutionId: number): Promise<HostelAccess> {
  const { data } = await privateClient.get(`${HOSTEL.BASE(institutionId)}/me`).catch(serverError);
  return data.access;
}

export async function fetchStaffCandidates(institutionId: number): Promise<{ id: number; name: string; roleName: string | null }[]> {
  const { data } = await privateClient.get(`${HOSTEL.BASE(institutionId)}/staff-candidates`).catch(serverError);
  return data.staff ?? [];
}
